import {
  booleanArray2DToNoteCollection,
  booleanArrayToNoteCollection,
  DRAFT_CONVERSION_NOTE_SCALE,
  Key,
  MAJOR_SCALE,
  MINOR_SCALE,
  Scale,
  TICKS_PER_LOOP
} from '@arpeggi/shared'
import { cast, Instance, types } from 'mobx-state-tree'
import { ForDirectExtend } from '../../../../features/mobx'
import { Octave } from '../../../client/genesis/genesis_proto_pb'
import { Arp2Utils } from '../../../client/SongUtils'
import { INoteStore, NoteStore, NoteUpdateType } from './NoteStore'

const MAX_POLYPHONY = 8

// A collection of single note instances
const _NoteCollectionStore = types
  .model({
    notes: types.array(NoteStore)
  })
  .views((self) => ({
    getNoteByID(id: string): INoteStore {
      return self.notes.find((note) => note.uniqueId === id)
    }
  }))
  .actions((self) => {
    function initialize() {}
    function clearNotes() {
      self.notes.clear()
    }
    function updateScale(scale: Scale, key: Key) {
      // When switching between scales, we need to set all out-of-scale notes to null.
      const notesToRemove: string[] = []
      self.notes.forEach((note) => {
        if (!inScale(note, scale, key)) {
          notesToRemove.push(note.uniqueId)
        }
      })
      removeNotes(notesToRemove)
    }

    function removeNotes(noteIDs: string[]) {
      self.notes = cast(self.notes.filter((note) => !noteIDs.includes(note.uniqueId)))
    }

    // Positive offset means transposing up in pitch.
    function transposeBy(offset: number) {
      self.notes.forEach((note) => {
        note.transposeBy(offset)
      })
    }

    function addNote(values: NoteUpdateType) {
      const newNote = NoteStore.create(values)
      self.notes.push(newNote)
    }

    /**
     * Populates the note collection from an array of booleans, where the array represents an entire loop
     * this populates every note with the value MIDDLE_C
     */
    function populateFromDrumBooleanArray(arr: boolean[]) {
      clearNotes()
      const newNotes = booleanArrayToNoteCollection(arr)
      newNotes.notes.forEach((newNote) => {
        addNote(newNote)
      })
    }

    /**
     * Populates the note collection from an array of arrays of booleans, just like how we used
     * to represent NoteRowStore
     */
    function populateFrom2DBooleanArray(arr2D: boolean[][]) {
      clearNotes()
      const newNotes = booleanArray2DToNoteCollection(arr2D)
      newNotes.notes.forEach((newNote) => {
        addNote(newNote)
      })
    }

    /**
     * Populates the note collection from a one dimensional number array, `notes`.
     * If numberArr[i] = n and is non-zero, add a note to the note collection
     */
    function populateFromNumberArray(numberArr: number[], octave = Octave.ZERO, key: Key = Key.C) {
      clearNotes()
      numberArr.forEach((note, beat) => {
        if (numberArr[beat] <= 0) return

        const beatLengthTicks = (1 / numberArr.length) * TICKS_PER_LOOP
        addNote({
          start: beat * beatLengthTicks,
          duration: beatLengthTicks * DRAFT_CONVERSION_NOTE_SCALE,
          note: Arp2Utils.adjustNoteToGlobal(note + key, octave) - 1
        })
      })
    }

    function updateNote(noteID: string, newValues: NoteUpdateType): boolean {
      const note = self.getNoteByID(noteID)
      if (!note) return

      // If adding a note and already maxed voices, don't add.
      const updatingStart = newValues.start !== undefined && newValues.start !== null
      const updatingDuration = newValues.duration !== undefined && newValues.duration !== null
      if (
        getVoicesInRange(
          updatingStart ? newValues.start : note.start,
          updatingDuration ? newValues.duration : note.duration
        ) >= MAX_POLYPHONY
      ) {
        return false
      }
      note.update(newValues)
      removeNotes(notesOverlappingRange(note.start, note.duration).map((note) => note.uniqueId))
      return true
    }

    function getVoicesInRange(start: number, duration: number): number {
      let voices = 0
      self.notes.forEach((note) => {
        if (noteOverlapsRange(note, start, duration)) {
          voices++
        }
      })
      return voices
    }

    /**
     * returns a list of all notes overlapping with the provided [start, start + duration] range
     */
    function notesOverlappingRange(start: number, duration: number): INoteStore[] {
      return self.notes.filter((note) => noteOverlapsRange(note, start, duration))
    }

    function minimizeForStorage() {
      return
    }

    return {
      initialize,
      removeNotes,
      addNote,
      clearNotes,
      updateNote,
      updateScale,
      populateFromDrumBooleanArray,
      populateFromNumberArray,
      minimizeForStorage,
      transposeBy,
      notesOverlappingRange,
      populateFrom2DBooleanArray
    }
  })

interface NoteCollectionStoreType extends ForDirectExtend<typeof _NoteCollectionStore> {}
export const NoteCollectionStore: NoteCollectionStoreType = _NoteCollectionStore

export interface INoteCollectionStore
  extends ForDirectExtend<Instance<typeof NoteCollectionStore>> {}

/**
 * Returns true if the note is in scale, given the Scale and Key we're currently in, false otherwise.
 */
const inScale = (note: INoteStore, scale: Scale, key: Key) => {
  if (scale === Scale.CHROMATIC) {
    return true
  } else if (scale === Scale.MAJOR) {
    return MAJOR_SCALE[(note.note - key + 12) % 12]
  } else if (scale === Scale.MINOR) {
    return MINOR_SCALE[(note.note - key + 12) % 12]
  }
}

/**
 * Returns true if the selected note overlaps with the provided [start, start + duration] range
 * @param note The note to check whether it overlaps with the range
 * @param start The start of the range (in ticks)
 * @param duration The duration of the range (in ticks)
 */
const noteOverlapsRange = (note: INoteStore, start: number, duration: number): boolean => {
  const end = start + duration
  return (
    (note.start >= start && note.start <= end) ||
    (note.end <= end && note.end >= start) ||
    (note.start <= start && note.end >= end)
  )
}
